import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Button, Grid, Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CreateBookingsForOffer } from "../../../components/form/CreateBookingForm/CreateBookingsForOffer";
import { CustomerSelectionFormControl } from "../../../components/form/CustomerSelectionFormControl/CustomerSelectionFormControl";
import { DiscountFormControl } from "../../../components/form/DiscountFormControl";
import { ProductSelectionFormControl } from "../../../components/form/ProductSelectionFormControl/ProductSelectionFormControl";
import { STATUS_KEYS } from "../../../schemas/status";
import { useProductsByIds } from "../../../services/productsService";
import { SendOfferTab } from "./SendOfferTab";

export const DialogTabsEnum = ((DialogTabsEnum) => {
    DialogTabsEnum[(DialogTabsEnum.CUSTOMER_SELECTION = 0)] = "CUSTOMER_SELECTION";
    DialogTabsEnum[(DialogTabsEnum.PRODUCT_SELECTION = 1)] = "PRODUCT_SELECTION";
    DialogTabsEnum[(DialogTabsEnum.DISCOUNT = 2)] = "DISCOUNT";
    DialogTabsEnum[(DialogTabsEnum.SEND_OFFER = 3)] = "SEND_OFFER";
    DialogTabsEnum[(DialogTabsEnum.CREATE_INITIAL_BOOKING = 4)] = "CREATE_INITIAL_BOOKING";
    return DialogTabsEnum;
})({});

export const DialogTabs = (props) => {
    const {
        control,
        fields,
        offer,
        onSaveAndSend,
        readOnly = false,
        initialTab,
        selectedTab: externalSelectedTab,
        setSelectedTab: externalSetSelectedTab,
        isRequestSend = false,
        getRequestDataForGenerateOffer,
        bodyEditorRef = null,
    } = props;

    const { t } = useTranslation();
    const enableBookingsTab = offer != null;

    const [internalSelectedTab, setInternalSelectedTab] = useState(
        initialTab ? initialTab : DialogTabsEnum.CUSTOMER_SELECTION,
    );

    const selectedTab = externalSelectedTab ?? internalSelectedTab;
    const setSelectedTab = externalSetSelectedTab ?? setInternalSelectedTab;

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    const { setValue } = useFormContext();

    const productDiscounts = useWatch({ name: fields.discount }) || {};
    const selectedProductIds = Object.keys(productDiscounts);
    const areTabsDisabled = useAreTabsDisabled(fields, enableBookingsTab);

    const canGoToPreviousTab =
        selectedTab > DialogTabsEnum.CUSTOMER_SELECTION && !areTabsDisabled[selectedTab - 1];
    const canGoToNextTab =
        selectedTab < DialogTabsEnum.CREATE_INITIAL_BOOKING && !areTabsDisabled[selectedTab + 1];

    const handleGoToPreviousTab = () => {
        if (canGoToPreviousTab) {
            setSelectedTab(selectedTab - 1);
        }
    };

    const handleGoToNextTab = () => {
        if (canGoToNextTab) {
            setSelectedTab(selectedTab + 1);
        }
    };

    const handleSetSelectedProductIds = (productIds) => {
        const productIdsToAdd = productIds.filter((id) => !selectedProductIds.includes(id));
        const productIdsToRemove = selectedProductIds.filter((id) => !productIds.includes(id));

        const updatedDiscounts = { ...productDiscounts };
        for (const productId of productIdsToAdd) {
            updatedDiscounts[productId] = 0;
        }
        for (const productId of productIdsToRemove) {
            delete updatedDiscounts[productId];
        }

        setValue(fields.discount, updatedDiscounts);
    };

    return (
        <Grid container direction="column">
            <Grid item>
                <Tabs value={selectedTab} onChange={handleTabChange}>
                    <Tab
                        label={t("offers.DialogTabs.customerSelection")}
                        disabled={areTabsDisabled[DialogTabsEnum.CUSTOMER_SELECTION]}
                    />
                    <Tab
                        label={t("offers.DialogTabs.productSelection")}
                        disabled={areTabsDisabled[DialogTabsEnum.PRODUCT_SELECTION]}
                    />
                    <Tab
                        label={t("offers.DialogTabs.discount")}
                        disabled={areTabsDisabled[DialogTabsEnum.DISCOUNT]}
                    />
                    {!readOnly && (
                        <Tab
                            label={t("offers.DialogTabs.sendOffer")}
                            disabled={areTabsDisabled[DialogTabsEnum.SEND_OFFER]}
                        />
                    )}
                    {!readOnly && (
                        <Tab
                            label={t("offers.DialogTabs.createInitialBooking")}
                            disabled={areTabsDisabled[DialogTabsEnum.CREATE_INITIAL_BOOKING]}
                        />
                    )}
                </Tabs>
            </Grid>
            <Grid item sx={{ my: 2 }}>
                <TabPanel activeTab={selectedTab} tab={DialogTabsEnum.CUSTOMER_SELECTION}>
                    <CustomerSelectionFormControl
                        control={control}
                        fieldName={fields.customerIds}
                        readOnly={readOnly}
                    />
                </TabPanel>
                <TabPanel activeTab={selectedTab} tab={DialogTabsEnum.PRODUCT_SELECTION}>
                    <ProductSelectionFormControl
                        multipleApartments={true}
                        selectedProductIds={selectedProductIds}
                        setSelectedProductIds={handleSetSelectedProductIds}
                        readOnly={readOnly}
                        excludedStatuses={[
                            STATUS_KEYS.Sold,
                            STATUS_KEYS.Reservation,
                        ]}
                    />
                </TabPanel>
                <TabPanel activeTab={selectedTab} tab={DialogTabsEnum.DISCOUNT}>
                    <DiscountFormControl
                        fields={{ discount: fields.discount }}
                        readonly={readOnly}
                    />
                </TabPanel>
                {!readOnly && (
                    <TabPanel activeTab={selectedTab} tab={DialogTabsEnum.SEND_OFFER}>
                        {offer && onSaveAndSend && (
                                <SendOfferTab offer={offer} onSaveAndSend={onSaveAndSend} isRequestSend={isRequestSend} getRequestDataForGenerateOffer={getRequestDataForGenerateOffer} bodyEditorRef={bodyEditorRef}/>
                        )}
                    </TabPanel>
                )}
                {!readOnly && (
                    <TabPanel activeTab={selectedTab} tab={DialogTabsEnum.CREATE_INITIAL_BOOKING}>
                        {offer && <CreateBookingsForOffer offer={offer} readOnly={readOnly} />}
                    </TabPanel>
                )}
            </Grid>
            {!readOnly && (
                <Grid container justifyContent={"space-between"} gap={2}>
                    <Grid item xs>
                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<KeyboardArrowLeftIcon />}
                            onClick={handleGoToPreviousTab}
                            disabled={!canGoToPreviousTab}
                        >
                            {t("offers.DialogTabs.back")}
                        </Button>
                    </Grid>
                    <Grid item xs>
                        <Button
                            variant="contained"
                            fullWidth
                            endIcon={<KeyboardArrowRightIcon />}
                            onClick={handleGoToNextTab}
                            disabled={!canGoToNextTab}
                        >
                            {t("offers.DialogTabs.continue")}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

const TabPanel = (props) => {
    const { children, activeTab, tab } = props;
    const isHidden = activeTab !== tab;
    return (
        <Box hidden={isHidden} sx={{ display: isHidden ? "none" : "block" }}>
            {children}
        </Box>
    );
};

const useAreTabsDisabled = (fields, doesOfferExist) => {
    const selectedCustomerIds = useWatch({ name: fields.customerIds });
    const discounts = useWatch({ name: fields.discount });
    const selectedProductIds = Object.keys(discounts);

    const { data: selectedProducts } = useProductsByIds(selectedProductIds);

    const isAnyCustomerSelected = selectedCustomerIds && selectedCustomerIds.length > 0;
    const isAnyApartmentSelected =
        selectedProductIds.length > 0 &&
        selectedProducts != null &&
        selectedProducts.some((p) => p.is_apartment);
    const areDiscountEntriesCreated = Object.keys(discounts).length === selectedProductIds.length;

    const isCustomerSelectionTabDisabled = false; // Always enabled
    const isProductsTabDisabled = !isAnyCustomerSelected;
    const isDiscountTabDisabled = !isAnyApartmentSelected || isProductsTabDisabled;
    const isSendOfferTabDisabled =
        !doesOfferExist || !areDiscountEntriesCreated || isDiscountTabDisabled;
    const isCreateInitialBookingTabDisabled = !doesOfferExist;

    // Index by enum entries
    return [
        isCustomerSelectionTabDisabled,
        isProductsTabDisabled,
        isDiscountTabDisabled,
        isSendOfferTabDisabled,
        isCreateInitialBookingTabDisabled,
    ];
};
